/**
 * One corrective action for several No answers (Shane Humphries, feedback
 * 2026-09-24: "loose trash applies to both the procedural and blow-off
 * questions"; JIM-1008-06).
 *
 * A second No that is the same problem points at the question that carries
 * the corrective action instead of adding its own. At submit the pointing
 * question files as "needs attention — same issue as …" with no corrective
 * action of its own (carried forward, so the sync touches nothing on it), and the carrying question's corrective action notes say
 * which other questions it covers. The report and the open-actions list then
 * hold one row for the problem; resolution follows the carrying question.
 */

export type SameIssueResponse = {
  questionId: string;
  value: unknown;
  correctiveActions?: Array<{ notes?: string; location?: string }>;
  sameAsQuestionId?: string;
};

export type SameIssueQuestion = { id: string; question: string };

/** Other No answers in this visit that carry their own corrective action. */
export function sameIssueChoices(
  responses: Record<string, SameIssueResponse | undefined>,
  questions: SameIssueQuestion[],
  forQuestionId: string
): SameIssueQuestion[] {
  return questions.filter((q) => {
    if (q.id === forQuestionId) return false;
    const r = responses[q.id];
    return (
      !!r &&
      r.value === false &&
      !r.sameAsQuestionId &&
      (r.correctiveActions?.length ?? 0) > 0
    );
  });
}

/** Carrying question id → the texts of the questions it also covers. */
export function coveredQuestions(
  responses: Record<string, SameIssueResponse | undefined>,
  questions: SameIssueQuestion[]
): Record<string, string[]> {
  const text = new Map(questions.map((q) => [q.id, q.question]));
  const out: Record<string, string[]> = {};
  for (const r of Object.values(responses)) {
    if (!r || r.value !== false || !r.sameAsQuestionId) continue;
    (out[r.sameAsQuestionId] ??= []).push(
      text.get(r.questionId) ?? r.questionId
    );
  }
  return out;
}

export function alsoCoversLine(texts: string[]): string {
  return texts.length ? `Also covers: ${texts.join('; ')}` : '';
}

/** How a finding changes when its question points at another, or is pointed at. */
export function applySameIssue<
  F extends {
    condition: string;
    description: string;
    requires_correction: boolean;
    correction_notes: string | null;
    carried_forward: boolean;
  },
>(
  finding: F,
  questionText: string,
  link: { sameAs?: string | null; covers?: string[] }
): F {
  if (link.sameAs) {
    return {
      ...finding,
      description: `${questionText}: Needs attention — same issue as: ${link.sameAs}`,
      // The submit handler sets requires_correction from the condition
      // (poor/failed), not from the flag; "fair" keeps this one off the
      // open-findings list the next visit reads.
      condition: 'fair',
      requires_correction: false,
      correction_notes: null,
      // The API's corrective-action sync reads a finding without
      // requires_correction as a clean answer and resolves the question's
      // open actions; carried_forward makes it leave this one alone.
      carried_forward: true,
    };
  }
  if (link.covers?.length) {
    const line = alsoCoversLine(link.covers);
    return {
      ...finding,
      description: `${finding.description}\n${line}`,
      correction_notes: [finding.correction_notes, line]
        .filter(Boolean)
        .join('\n'),
    };
  }
  return finding;
}
